<!-- Full-height page: :host is 100% of the layout's content area; the flex
     column chain (flex-1 + min-h-0 at each level) hands the remaining height
     down to the list, which scrolls internally. -->
<section class="p-6 h-full flex flex-col">
  <div class="bg-white rounded-xl flex-1 min-h-0 flex flex-col">

    <!-- Existing Filters Display -->
    <div class="shrink-0 flex flex-row justify-between items-start w-full">
      <div class="-m-1 flex flex-wrap items-center mb-0 w-full">

        <!-- Filters -->
        <ng-container *ngIf="searchFilters$ | async as filters">
          <ng-container *ngIf="filterTypes$ | async as types">
            <span *ngFor="let filter of getSelectedFilters(filters)" class="m-1 inline-flex items-center rounded-full bg-gray-50 py-1.5 px-3 text-xs font-medium text-gray-500">
              <!-- Add a special highlight for person filters -->
              <span [ngClass]="{'text-secondary': isPersonFilter(filter.filterType, types)}">
                {{ getFilterLabel(filter.filterType, types) }}: {{ getFilterValue(filter, types) }}
              </span>
            </span>
          </ng-container>

        </ng-container>

        <a routerLink="/advanced-search" class="ml-1 bg-blue-royal p-1.5 rounded-full" matTooltip="Edit Filters" matTooltipPosition="above" matTooltipClass="custom-tooltip">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-3 text-white">
            <path d="m2.695 14.762-1.262 3.155a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.886L17.5 5.501a2.121 2.121 0 0 0-3-3L3.58 13.419a4 4 0 0 0-.885 1.343Z" />
          </svg>
        </a>
      </div>

      <div class="flex items-center gap-3">
        <span class="text-sm font-poppins font-normal text-secondary whitespace-nowrap">
          <span class="font-bold font-poppins">{{ (totalRecords$ | async) | number:'1.0-0' }}</span> results found
        </span>

        <!-- Export Button -->
        <button
          (click)="openExportModal()"
          [disabled]="!(totalRecords$ | async) || (totalRecords$ | async) === 0"
          [class.opacity-50]="!(totalRecords$ | async) || (totalRecords$ | async) === 0"
          [class.cursor-not-allowed]="!(totalRecords$ | async) || (totalRecords$ | async) === 0"
          class="btn btn-rounded flex items-center space-x-2 text-sm whitespace-nowrap">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4">
            <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
            <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
          </svg>
          <span>Export</span>
        </button>

        <!-- My prospecting lists -->
        <div class="relative" *ngIf="prospectingLists.length">
          <button type="button" (click)="toggleProspectingLists()"
                  class="btn btn-rounded flex items-center space-x-1 text-sm whitespace-nowrap">
            <span>My prospecting lists ({{ prospectingLists.length }})</span>
            <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
          <div *ngIf="isProspectingListsOpen" class="fixed inset-0 z-30" (click)="isProspectingListsOpen = false"></div>
          <div *ngIf="isProspectingListsOpen"
               class="absolute right-0 mt-2 w-80 max-h-80 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg z-40 py-1">
            <button *ngFor="let list of prospectingLists" type="button"
                    class="w-full text-left px-4 py-2.5 hover:bg-gray-50"
                    (click)="openProspectingList(list)">
              <span class="block text-sm font-medium text-gray-800 truncate">{{ list.summary_label || 'Prospecting list' }}</span>
              <span class="block text-xs text-gray-400">
                {{ list.leads_count | number:'1.0-0' }} lead{{ list.leads_count === 1 ? '' : 's' }}
                · {{ list.total_companies | number:'1.0-0' }} companies
                <ng-container *ngIf="list.active_export_id"> · export running</ng-container>
              </span>
            </button>
          </div>
        </div>

        <!-- Proceed to Leads (primary) -->
        <button *ngIf="(totalRecords$ | async) as total"
                type="button"
                (click)="openProceedModal()"
                class="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 whitespace-nowrap">
          <span>Proceed to Leads</span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4">
            <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>

    <div class="flex flex-col flex-1 min-h-0 mx-auto w-full max-w-7xl px-0 sm:px-6 lg:px-8 py-8">

      <div class="flex flex-col flex-1 min-h-0">

        <!-- Loading State - Show this when initially loading OR when store loading is true -->
        <ng-container *ngIf="isInitialLoading || (loading$ | async)">
          <!-- Skeleton fills the same full-height box as the real list; extra
               rows are clipped (overflow-hidden) so it never shows a scrollbar
               and looks "full" at any viewport height. -->
          <ul class="border border-gray-100 rounded-xl px-3 divide-y divide-gray-100 flex-1 min-h-0 overflow-hidden">
            <li class="flex justify-between gap-x-3 py-3" *ngFor="let _ of skeletonRows">
              <div class="lg:flex lg:flex-row justify-between items-center lg:space-x-3 w-full animate-pulse">
                <div class="flex flex-row space-x-3 bg-gray-50 rounded-lg p-3 w-full lg:w-3/6">
                  <div class="flex flex-col w-full">
                    <!-- Top row with logo and company info -->
                    <div class="flex justify-between items-start w-full">
                      <div class="flex items-center space-x-3">
                        <!-- Logo skeleton -->
                        <div class="h-10 w-10 md:h-12 md:w-12 flex-none rounded-md bg-gray-100"></div>
                        <!-- Company name and location skeleton -->
                        <div class="flex flex-col space-y-2">
                          <div class="h-4 w-32 bg-gray-100 rounded"></div>
                          <div class="h-3 w-24 bg-gray-100 rounded"></div>
                        </div>
                      </div>
                      <!-- Info button skeleton -->
                      <div class="h-8 w-8 bg-gray-100 rounded-md"></div>
                    </div>

                    <!-- Divider and additional info row -->
                    <div class="mt-2 pt-2 border-t border-gray-100">
                      <div class="flex items-center space-x-3">
                        <!-- Website icon skeleton -->
                        <div class="h-5 w-5 bg-gray-100 rounded"></div>
                        <!-- Additional info skeleton -->
                        <div class="flex items-center space-x-2">
                          <div class="h-3 w-20 bg-gray-100 rounded"></div>
                          <div class="h-3 w-16 bg-gray-100 rounded"></div>
                          <div class="h-3 w-24 bg-gray-100 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Search people button skeleton -->
                <div class="h-10 w-48 bg-gray-100 rounded-full mt-1.5 lg:mt-0"></div>
              </div>
            </li>
          </ul>
        </ng-container>

        <!-- Results State - Show ONLY when NOT initially loading AND store is not loading -->
        <ng-container *ngIf="!isInitialLoading && !(loading$ | async)">
          <!-- Check if we have search filters (meaning a search was performed) -->
          <ng-container *ngIf="searchFilters$ | async as searchFilters">

            <!-- Show results if we have companies -->
            <ng-container *ngIf="(companies$ | async) as companies">
              <ng-container *ngIf="companies.length > 0">
                <ul #companiesList
                    (scroll)="onListScroll($event)"
                    class="border border-gray-100 rounded-xl px-3 divide-y divide-gray-100 flex-1 min-h-0 overflow-y-auto overscroll-contain transition-all duration-400 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 hover:scrollbar-thumb-transparent">
                  <li *ngFor="let company of companies" class="flex justify-between gap-x-3 py-3">
                    <div class="lg:flex lg:flex-row justify-between items-center lg:space-x-3 w-full">
                      <div class="flex flex-row space-x-3 bg-gray-50 rounded-lg p-3 w-full lg:w-3/6">

                        <div class="flex flex-col w-full">
                          <div class="flex justify-between items-start w-full">

                            <div class="flex items-center space-x-3">
                              <img
                                [src]="'https://ui-avatars.com/api/?background=FFA500&color=fff&rounded=false&name=' + (company.company_name?.substring(0, 2) || 'NA') + '&format=svg&size=128'"
                                [alt]="company.company_name"
                                class="h-10 w-10 md:h-12 md:w-12 flex-none rounded-md bg-gray-50"
                              />
                              <div>
                                <p class="font-poppins font-bold text-gray-700">{{ company.company_name }}</p>
                                <p class="text-gray-500 text-xs font-normal font-poppins">
                                  {{ getLocation(company.main_address) }}
                                </p>
                              </div>
                            </div>

                            <div class="flex">
                              <button (click)="openCompanyDetails(company)" class="bg-gray-100 p-2 rounded-md hover:opacity-90" matTooltip="Information" matTooltipPosition="above" matTooltipClass="custom-tooltip">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-primary">
                                  <path fill-rule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clip-rule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          </div>

                          <!-- Additional info row -->
                          <div class="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
                            <div class="flex items-center space-x-3">
                              <a *ngIf="company.website_url" [href]="company.website_url" target="_blank" class="bg-gray-100 p-1 rounded hover:opacity-90">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-3 text-gray-400">
                                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
                                </svg>
                              </a>
                              <div class="flex items-center space-x-2 text-xs text-gray-400 font-normal font-poppins">
                                <span *ngIf="company.year_founded" class="font-poppins">Founded {{ company.year_founded }}</span>
                                <span *ngIf="company.year_founded && (company.num_locations || company.employee_count?.value || company.revenue?.value)" class="text-gray-100">•</span>
                                <span *ngIf="company.num_locations" class="font-poppins">{{ company.num_locations }} {{ company.num_locations === 1 ? 'Location' : 'Locations' }}</span>
                                <span *ngIf="company.num_locations && (company.employee_count?.value || company.revenue?.value)" class="text-gray-100">•</span>
                                <span *ngIf="company.employee_count?.value" class="font-poppins">{{ formatEmployeeCount(company.employee_count.value) }} Employees</span>
                                <span *ngIf="company.employee_count?.value && company.revenue?.value" class="text-gray-100">•</span>
                                <span *ngIf="company.revenue?.value" class="font-poppins">${{ formatRevenue(company.revenue.value) }} Revenue</span>
                              </div>
                            </div>
                            <a *ngIf="company?.linkedin_url" [href]="company.linkedin_url" target="_blank"
                               class="bg-gray-50/50 p-1 rounded-lg text-primary hover:opacity-90 transition-colors flex justify-center items-center">
                              <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
                              </svg>
                            </a>
                          </div>
                        </div>
                      </div>
                      <div class="flex flex-col items-center lg:items-end gap-1 mt-1.5 lg:mt-0">
                        <a [routerLink]="['/advanced-search/companies', company.veridion_id, 'people']" (click)="searchPeople(company.company_name, company)" class="cursor-pointer btn btn-rounded">
                          Search people from {{ company.company_name }}
                        </a>

                        <!-- People-count preview: same Apollo query, shows only the total.
                             btn btn-rounded = transparent bg, gray only on hover; px-6 from .btn
                             keeps the text edge aligned with the button above. -->
                        <button *ngIf="!getPeopleCount(company)" type="button" (click)="showPeopleCount(company)"
                                class="btn btn-rounded !py-1 !text-xs font-poppins">
                          Show people count
                        </button>
                        <span *ngIf="getPeopleCount(company)?.status === 'loading'"
                              class="btn btn-rounded !py-1 !text-xs !text-gray-400 font-poppins animate-pulse">
                          Counting people…
                        </span>
                        <span *ngIf="getPeopleCount(company)?.status === 'loaded'"
                              class="btn btn-rounded !py-1 !text-xs !text-gray-500 font-poppins">
                          <span class="font-bold !text-xs mr-1">{{ getPeopleCount(company)?.count | number:'1.0-0' }}</span> people found
                        </span>
                        <button *ngIf="getPeopleCount(company)?.status === 'error'" type="button" (click)="showPeopleCount(company)"
                                class="btn btn-rounded !py-1 !text-xs !text-red-500 font-poppins">
                          Couldn't get count — tap to retry
                        </button>
                      </div>
                    </div>
                  </li>
                </ul>
              </ng-container>

              <!-- Show "No companies found" ONLY if we have 0 companies after search -->
              <ng-container *ngIf="companies.length === 0">
                <div class="flex flex-col flex-1 min-h-0 items-center justify-center py-8 border border-gray-100 rounded-xl px-3">
                  <p class="text-gray-500 text-md mb-4">No company was found.</p>
                  <a routerLink="/advanced-search" class="btn btn-rounded flex items-center space-x-1 text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-blue-royal">
                      <path fill-rule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clip-rule="evenodd" />
                    </svg>
                    <span class="text-blue-royal !font-normal">Back to search</span>
                  </a>
                </div>
              </ng-container>
            </ng-container>

          </ng-container>
        </ng-container>

        <!-- Pagination - Show when not initially loading, store not loading, and we have results -->
        <div class="shrink-0 flex justify-between items-center mt-4 mb-4"
             *ngIf="!isInitialLoading && !(loading$ | async) && (companies$ | async)?.length > 0">
          <!-- Records count on the left -->
          <div class="text-sm font-poppins text-gray-500">
            <span class="font-semibold">{{ (totalRecords$ | async) | number:'1.0-0' }}</span>
            results
          </div>

          <!-- Previous/Next buttons on the right -->
          <nav class="inline-flex gap-2" aria-label="Pagination">
            <!-- Previous Button -->
            <button
              type="button"
              (click)="onPreviousPage()"
              [disabled]="!(previousPageToken$ | async)"
              [class.opacity-50]="!(previousPageToken$ | async)"
              [class.cursor-not-allowed]="!(previousPageToken$ | async)"
              class="relative inline-flex items-center justify-center w-24 rounded-md px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 focus:z-20 focus:outline-offset-0">
              Previous
            </button>

            <!-- Next Button -->
            <button
              type="button"
              (click)="onNextPage()"
              [disabled]="!(nextPageToken$ | async)"
              [class.opacity-50]="!(nextPageToken$ | async)"
              [class.cursor-not-allowed]="!(nextPageToken$ | async)"
              class="relative inline-flex items-center justify-center w-24 rounded-md px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 focus:z-20 focus:outline-offset-0">
              Next
            </button>
          </nav>
        </div>

      </div>

      <div class="shrink-0 w-full mt-6 flex justify-between flex-row mb-6">

        <div>
          <a routerLink="/advanced-search" class="lg:relative lg:right-6 btn btn-rounded flex items-center space-x-1 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-blue-royal">
              <path fill-rule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clip-rule="evenodd" />
            </svg>
            <span class="text-blue-royal !font-normal">Back</span>
          </a>
        </div>

      </div>

    </div>
  </div>
</section>

<!-- Company Details Modal - Outside section for proper full-page display -->
<app-company-details-modal
  [isVisible]="isModalVisible"
  [company]="selectedCompany"
  (modalClosed)="closeModal()">
</app-company-details-modal>

<!-- Export Modal -->
<app-export-modal
  [isVisible]="isExportModalVisible"
  [totalRecords]="(totalRecords$ | async) || 0"
  [filters]="currentSearchFilters"
  (modalClosed)="closeExportModal()">
</app-export-modal>

<!-- Proceed to Leads -->
<app-proceed-to-leads-modal
  [isVisible]="isProceedModalVisible"
  [totalRecords]="(totalRecords$ | async) || 0"
  [filters]="proceedFilters"
  [summaryLabel]="proceedSummaryLabel"
  (modalClosed)="closeProceedModal()">
</app-proceed-to-leads-modal>
